import { useMemo } from "react";
import type { ReactNode } from "react";
import { Badge } from "react-bootstrap";
import { FaArrowRight } from "react-icons/fa";
import { MdRotateLeft } from "react-icons/md";
import { useClientTable, type AppColumnDef } from "../../../../components/table/tableFeatures";
import { formatCurrency, formatNumber, formatPercent } from "../../../../utils/formatNumber";
import { pathLabelMapping } from "../../utils/pathToPurchase/utils";
import PathToPurchaseExpander from "./PathToPurchaseExpander";
import ProgressBar from "../../../../components/core/basic/ProgressBar";
import { type ConversionPathData } from "../../utils/pathToPurchase/types";
import { Table } from "../../../../components/table/Table";
import { RT8Pagination } from "../../../../components/table/Pagination";
import { RT8ColumnSelector } from "../../../../components/core/blocks/MetricSelector";
import { RT8FilterWidget } from "../../../merchandising/components/tables/FilterWidget";
import { SearchInput } from "../../../../components/fields/base/TextField";
import { Button } from "../../../../components/core/basic/Button";
import { resetTable } from "../../../../components/table/utils";
import { customFilterFn } from "../../../../components/table/filterFns";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { css } from "../../../../../styled-system/css";
import { HStack, VStack } from "../../../../../styled-system/jsx";
import { useTranslation } from "react-i18next";

interface Props {
  tableData: ConversionPathData[];
  scopeDescription: ReactNode;
  campaignReportId?: number;
}

interface MetricLimits {
  [key: string]: number;
  path_occurrences: number;
  impressions: number;
  total_cost: number;
  users_that_purchased: number;
  sales_amount: number;
  purchases: number;
  user_purchase_rate: number;
  ntb_users_that_purchased: number;
  ntb_sales_amount: number;
  ntb_purchases: number;
  ntb_percentage: number;
}

const PathToPurchaseTable = ({ tableData, scopeDescription, campaignReportId }: Props) => {
  const { t } = useTranslation(["amc"]);
  const TABLE_ID = "userConversionPathTable";

  const formattedTableData = useMemo<ConversionPathData[]>(() => {
    return tableData.map((row) => {
      if (!row.sales_amount) {
        return row;
      }
      const acos = row.total_cost / row.sales_amount;
      return { ...row, acos };
    });
  }, [tableData]);

  const metricLimits = useMemo(() => {
    return tableData.reduce((acc: MetricLimits, row) => {
      for (const key in row) {
        const value = row[key];

        if (typeof value === "string" && isNaN(parseFloat(value))) {
          continue;
        }

        if (typeof value === "number") {
          acc[key] = Math.max(acc[key] ?? -Infinity, value);
        } else if (typeof value === "string") {
          const parsedValue = parseFloat(value);
          if (!isNaN(parsedValue)) {
            acc[key] = Math.max(acc[key] ?? -Infinity, parsedValue);
          }
        }
      }
      return acc;
    }, {} as MetricLimits);
  }, [tableData]);

  const columns = useMemo<AppColumnDef<ConversionPathData>[]>(
    () => [
      {
        id: "path",
        header: t(($) => $.amc["Path"]),
        accessorKey: "path",
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        size: 600,
        cell: ({ row }) => {
          const { path } = row.original;

          const pathArray = path.match(/[A-Z]+-[A-Za-z]+/g)?.map((touchpoint: string) => {
            const key = touchpoint.split("-")[0] as keyof typeof pathLabelMapping;
            return pathLabelMapping[key];
          });

          return (
            <div style={{ display: "flex", fontWeight: "500", fontSize: "1.5rem" }}>
              {pathArray?.map((touchpoint, index) => (
                <div className="d-flex align-items-center" key={index}>
                  <Badge className={`badge bg-trellis-purple`} style={{ opacity: touchpoint.opacity }}>
                    {t(touchpoint.label)}
                  </Badge>{" "}
                  {index < pathArray.length - 1 ?
                    <FaArrowRight className="mx-2" />
                  : null}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        id: "path_occurrences",
        header: t(($) => $.amc["Path Occurrences"]),
        accessorKey: "path_occurrences",
        size: 250,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatNumber(value)}
              <ProgressBar progress={(value / metricLimits.path_occurrences) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      },
      {
        id: "impressions",
        header: t(($) => $.amc["Impressions"]),
        accessorKey: "impressions",
        size: 230,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatNumber(value)}
              <ProgressBar progress={(value / metricLimits.impressions) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      },
      {
        id: "cost",
        header: t(($) => $.amc["Cost"]),
        accessorFn: (row) => row.total_cost,
        size: 230,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatCurrency(value, {
                marketPlace: "USA",
                decimalPlaces: 0,
              })}
              <ProgressBar progress={(value / metricLimits.total_cost) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
        },
      },
      {
        id: "users_that_purchased",
        header: t(($) => $.amc["Users That Purchased"]),
        accessorKey: "users_that_purchased",
        size: 270,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatNumber(value)}
              <ProgressBar progress={(value / metricLimits.users_that_purchased) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      },
      {
        id: "sales_amount",
        header: t(($) => $.amc["Sales Amount"]),
        accessorKey: "sales_amount",
        size: 230,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatCurrency(value, {
                marketPlace: "USA",
                decimalPlaces: 0,
              })}
              <ProgressBar progress={(value / metricLimits.sales_amount) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
        },
      },
      {
        id: "purchases",
        header: t(($) => $.amc["Purchases"]),
        accessorKey: "purchases",
        size: 210,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatNumber(value)}
              <ProgressBar progress={(value / metricLimits.purchases) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      },
      {
        id: "acos",
        header: t(($) => $.amc["ACOS"]),
        accessorKey: "acos",
        size: 160,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatPercent(value, {
                decimalPlaces: 0,
              })}
              <ProgressBar progress={value * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      },
      {
        id: "user_purchase_rate",
        header: t(($) => $.amc["User Purchase Rate"]),
        accessorKey: "user_purchase_rate",
        size: 250,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatPercent(value, {
                decimalPlaces: 1,
              })}
              <ProgressBar progress={(value / metricLimits.user_purchase_rate) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      },
      {
        id: "ntb_users_that_purchased",
        header: t(($) => $.amc["NTB Users That Purchased"]),
        accessorKey: "ntb_users_that_purchased",
        size: 290,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {value}
              <ProgressBar progress={(value / metricLimits.ntb_users_that_purchased) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      },
      {
        id: "ntb_sales_amount",
        header: t(($) => $.amc["NTB Sales Amount"]),
        accessorKey: "ntb_sales_amount",
        size: 250,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatCurrency(value, {
                marketPlace: "USA",
                decimalPlaces: 0,
              })}
              <ProgressBar progress={(value / metricLimits.ntb_sales_amount) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
        },
      },
      {
        id: "ntb_purchases",
        header: t(($) => $.amc["NTB Purchases"]),
        accessorKey: "ntb_purchases",
        size: 230,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatNumber(value)}
              <ProgressBar progress={(value / metricLimits.ntb_purchases) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      },
      {
        id: "ntb_percentage",
        header: t(($) => $.amc["NTB Percentage"]),
        accessorKey: "ntb_percentage",
        size: 230,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <span className="d-flex align-items-center gap-3">
              {formatPercent(value, {
                decimalPlaces: 1,
              })}
              <ProgressBar progress={(value / metricLimits.ntb_percentage) * 100} width={65} />
            </span>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      },
    ],
    [metricLimits, t],
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: TABLE_ID,
    initialState: {
      sorting: [{ id: "impressions", desc: true }],
      columnPinning: { start: ["path"], end: [] },
      columnVisibility: {
        users_that_purchased: false,
        user_purchase_rate: false,
        ntb_users_that_purchased: false,
        ntb_sales_amount: false,
        ntb_purchases: false,
        ntb_percentage: false,
      },
    },
  });

  const table = useClientTable<ConversionPathData>({
    data: formattedTableData,
    columns,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    getRowCanExpand: () => true,
    getRowId: (row) => row.path,
    globalFilterFn: (row, _columnId, filterValue) => {
      if (!filterValue) {
        return true;
      }

      let searchable = "";
      try {
        searchable = row.original.path
          .replaceAll("SB", "Sponsored Brand")
          .replaceAll("SD", "Sponsored Display")
          .replaceAll("SP", "Sponsored Product");
      } catch {
        return false;
      }

      const normalizedFilterValue = String(filterValue);
      if (normalizedFilterValue.includes(",")) {
        const matchable = normalizedFilterValue.split(",");
        const normalizedSearchable = searchable.replaceAll(/\s/g, "").toLowerCase();
        return matchable.some((searchCriteria) => {
          const normalizedCriteria = searchCriteria.replaceAll(/\s/g, "").toLowerCase();
          return normalizedSearchable.includes(normalizedCriteria) && searchCriteria.trim().length;
        });
      }

      return searchable.toLowerCase().includes(normalizedFilterValue.toLowerCase());
    },
    initialState,
    state,
    ...setters,
  });

  return (
    <div>
      <HStack padding={"small"} fontSize={"large"} className={css({ position: "relative" })}>
        {scopeDescription}
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.amc["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={state.globalFilter ?? ""}
              onChange={(value) => {
                setters.onGlobalFilterChange(value);
              }}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({
          minWidth: "100%",
          "& th": {
            paddingY: "small",
          },
          "& td": {
            paddingY: "medium",
          },
        })}
        renderExpander={(row) => <PathToPurchaseExpander row={row.original} campaignReportId={campaignReportId} />}
      />
    </div>
  );
};

export default PathToPurchaseTable;
